import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ReactGA from 'react-ga';
import profileImage from '../../images/headshot.png';
import projectsData from '../../data/projectsData';
import HelmetComponent from '../helmet/HelmetComponent';

const COMMENT = '//';

const HomeLayout = () => {
    const history = useHistory();

    useEffect(() => {
        window.scrollTo(0, 0);
        ReactGA.set({ page: history.location.pathname });
        ReactGA.pageview(history.location.pathname);
    }, [history.location.pathname]);

    return (
        <>
            <HelmetComponent
                title="mattthewong"
                description="Senior distributed systems engineer. Builder. Lifelong student."
                URL="https://mattthewong.com"
            />
            <div>
                {/* Photo */}
                <div className="pt-16 mb-10">
                    <div className="relative group inline-block">
                        <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-red-900 rounded-full opacity-50 group-hover:opacity-75 blur transition duration-500" />
                        <img
                            src={profileImage}
                            alt="Matt Wong"
                            className="relative w-16 h-16 rounded-full object-cover border-2 border-neutral-800"
                        />
                    </div>
                </div>

                {/* Name */}
                <h1
                    className="text-3xl sm:text-4xl font-bold text-white tracking-tight font-mono"
                    style={{ marginBottom: '32px' }}
                >
                    {'> '}
                    <span className="text-red-500">matt wong</span>
                </h1>

                {/* Projects */}
                <div style={{ marginBottom: '80px' }}>
                    <h2 className="text-xs font-mono uppercase tracking-widest text-red-500" style={{ marginBottom: '32px' }}>
                        {COMMENT} projects
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: '24px' }}>
                        {projectsData.map((project) => {
                            return (
                                <a
                                    key={project.title}
                                    href={project.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="group block rounded-lg border border-red-900 bg-neutral-900 hover:border-red-500 hover:bg-neutral-900 transition-all duration-300"
                                    style={{ padding: '40px' }}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-lg font-mono font-bold text-white group-hover:text-red-500 transition-colors">
                                            {project.title}
                                        </h3>
                                        <svg className="w-4 h-4 text-neutral-600 group-hover:text-red-500 transition-colors transform group-hover:translate-x-1 group-hover:-translate-y-1 duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M17 7H7M17 7v10" />
                                        </svg>
                                    </div>
                                    <p className="text-sm text-neutral-400 leading-relaxed font-mono">
                                        {project.description}
                                    </p>
                                </a>
                            );
                        })}
                    </div>
                </div>

                {/* Fun facts */}
                <div style={{ marginBottom: '80px' }}>
                    <h2
                        className="text-xs font-mono uppercase tracking-widest text-red-500"
                        style={{ marginBottom: '32px' }}
                    >
                        {COMMENT} fun facts
                    </h2>
                    <div
                        className="grid"
                        style={{ gap: '24px' }}
                    >
                        <div
                            className="rounded-lg border border-red-900 bg-neutral-900"
                            style={{ padding: '40px' }}
                        >
                            <div className="flex justify-between items-baseline">
                                <p className="text-sm text-neutral-300 leading-relaxed font-mono">
                                    NCAA All-American in the long jump.{' '}<a href="https://trackandfield.mattthewong.com" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400 underline underline-offset-2">stats →</a>
                                </p>
                                <span className="text-xs font-mono text-red-500 ml-4 flex-shrink-0">2014</span>
                            </div>
                        </div>
                        <div
                            className="rounded-lg border border-red-900 bg-neutral-900"
                            style={{ padding: '40px' }}
                        >
                            <div className="flex justify-between items-baseline">
                                <p className="text-sm text-neutral-300 leading-relaxed font-mono">
                                    Lived in Tokyo for three months as a software engineer at MODE, immersing in the culture while building IoT infrastructure.
                                </p>
                                <span className="text-xs font-mono text-red-500 ml-4 flex-shrink-0">2019</span>
                            </div>
                        </div>
                        <div
                            className="rounded-lg border border-red-900 bg-neutral-900"
                            style={{ padding: '40px' }}
                        >
                            <div className="flex justify-between items-baseline">
                                <p className="text-sm text-neutral-300 leading-relaxed font-mono">
                                    Digital nomad across four cities &mdash; Denver, Boston, Kailua (Oahu), and Hoboken.
                                </p>
                                <span className="text-xs font-mono text-red-500 ml-4 flex-shrink-0">2023&ndash;2025</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Keyboard section */}
                <div style={{ marginBottom: '80px' }}>
                    <h2 className="text-xs font-mono uppercase tracking-widest text-red-500" style={{ marginBottom: '32px' }}>
                        {COMMENT} setup
                    </h2>
                    <a
                        href="https://configure.zsa.io/moonlander/layouts/GlzRQ/latest/0"
                        target="_blank"
                        rel="noreferrer"
                        className="group block rounded-lg border border-red-900 bg-neutral-900 hover:border-red-500 hover:bg-neutral-900 transition-all duration-300"
                        style={{ padding: '40px' }}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-mono font-bold text-white group-hover:text-red-500 transition-colors">
                                Moonlander MK1
                            </h3>
                            <svg className="w-4 h-4 text-neutral-600 group-hover:text-red-500 transition-colors transform group-hover:translate-x-1 group-hover:-translate-y-1 duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M17 7H7M17 7v10" />
                            </svg>
                        </div>
                        <p className="text-sm text-neutral-400 leading-relaxed font-mono">
                            Custom split ergonomic keyboard powered by Oryx. View my layout configuration.
                        </p>
                    </a>
                </div>
            </div>
        </>
    );
};

export default HomeLayout;
